import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";

/**
 * SkillSchema
 *
 * Matches the UnifiedSkill shape returned by GET /api/tools/skills,
 * extended with isFavorite for workspace favorites support.
 */
export const SkillSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  /**
   * WHICH OF THE THREE SOURCES THIS ROW CAME FROM — a union tag, never the
   * `AgentExternalTool.type` column. `EXTERNAL_TOOL` distinguishes an external
   * tool from an AI task (`TASK`) and a workflow (`FLOW`) in one merged feed;
   * `externalToolType` below is the column.
   */
  type: z.enum(["TASK", "EXTERNAL_TOOL", "FLOW"]),
  imageUrl: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  modelProvider: z.enum(DbEnum.MODEL_PROVIDER_VALUES).optional(),
  modelName: z.string().optional(),
  /**
   * The row's own `AgentExternalTool.type`. Present exactly when `type` is
   * `EXTERNAL_TOOL`; a TASK or FLOW row has no such column, which is why this is
   * optional rather than defaulted.
   *
   * 🚨 WITHOUT IT EVERY EXTERNAL TOOL IN THE FEED IS ONE UNDIFFERENTIATED KIND,
   * and an MCP server is indistinguishable from a webhook. `McpServer` is keyed
   * on `externalToolId` (its `@id`), so an MCP server's id IS an
   * `AgentExternalTool` id — a consumer routing on the id alone cannot tell the
   * two apart and sends both to the generic tool page. `ExternalToolDetailSchema`
   * already publishes this field on the DETAIL endpoint; only this feed dropped it.
   */
  externalToolType: z.enum(DbEnum.AGENT_EXTERNAL_TOOL_TYPE_VALUES).optional(),
  /**
   * The row's own `AITask.kind`. Present exactly when `type` is `TASK`, so a
   * card can name a decision task as one; an EXTERNAL_TOOL or FLOW row has no
   * such column, which is why this is optional rather than defaulted.
   */
  taskKind: z.enum(DbEnum.A_I_TASK_KIND_VALUES).optional(),
  isFavorite: z.boolean()
});

export type Skill = z.infer<typeof SkillSchema>;
